import {DataStatus, Dialog} from 'platform/components';

import {useRef, useState} from 'react';

import {
  isDefiniteNewVehicleRejection,
  newVehicleApi,
  useCreateFeatureMutation,
  usePutTrimFeatureAssignmentsMutation,
  useListMakeQuery,
  useListModelQuery,
  useListModelYearQuery,
  useListTrimQuery,
} from '@dms/api/new-vehicle';

import type {ApiError} from 'shared/web';

import {catalogueErrorMessage, handleCatalogueError, t} from './catalogueUi';
import {emptyEquipmentDraft, equipmentWriteBody, type EquipmentDraft} from './equipmentDraft';
import {NewEquipmentFlow} from './NewEquipmentFlow';

/** Reuses the guided form for a persisted equipment-library item and complete-set attachments. */
export function NewCatalogueEquipment(props: {
  modelYearId?: string;
  canViewCost: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const makes = useListMakeQuery({});
  const models = useListModelQuery({});
  const years = useListModelYearQuery({});
  const trims = useListTrimQuery({});
  const [createFeature] = useCreateFeatureMutation();
  const [putAssignments] = usePutTrimFeatureAssignmentsMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const createdId = useRef<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [error, setError] = useState<string>();
  const [submitted, setSubmitted] = useState<EquipmentDraft | null>(null);
  if (!makes.data || !models.data || !years.data || !trims.data) {
    return (
      <Dialog
        isOpen
        title={t('entity.newVehicle.authoring.newEquipment', 'New equipment')}
        onClose={props.onClose}
      >
        <DataStatus
          isLoading={makes.isLoading || models.isLoading || years.isLoading || trims.isLoading}
          isError={makes.isError || models.isError || years.isError || trims.isError}
          errorAction={{
            title: t('general.actions.retry', 'Retry'),
            onClick: () => {
              void makes.refetch();
              void models.refetch();
              void years.refetch();
              void trims.refetch();
            },
          }}
        />
      </Dialog>
    );
  }
  const carScope = {
    makes: makes.data.items.filter((make) => !make.archived),
    models: models.data.items.filter((model) => !model.archived),
    modelYears: years.data.items.filter((year) => !year.archived && year.status !== 'archived'),
  };
  const initialYear = carScope.modelYears.find((year) => year.id === props.modelYearId);
  const initialModel = carScope.models.find(
    (model) =>
      model.id === initialYear?.modelId && carScope.makes.some((make) => make.id === model.makeId)
  );
  const availableTrims = trims.data.items.filter((trim) => !trim.archived);
  return (
    <NewEquipmentFlow
      canViewCost={props.canViewCost}
      trims={availableTrims}
      carScope={carScope}
      initialValues={{
        ...emptyEquipmentDraft,
        scope: {
          makeId: initialModel?.makeId ?? '',
          modelId: initialModel?.id ?? '',
          modelYearId: initialModel ? (initialYear?.id ?? '') : '',
        },
      }}
      scopeLabel={t(
        'entity.newVehicle.authoring.chooseCarScope',
        'Choose which cars this equipment is for, or skip to keep it in the library only.'
      )}
      ownershipMessage={t(
        'entity.newVehicle.authoring.libraryOwnership',
        'This creates an equipment library item. Select trims to make it available on those cars.'
      )}
      errorMessage={error}
      isSaveDisabled={unknownOutcome}
      isIntentLocked={submitted !== null}
      onClose={props.onClose}
      onSave={async (values) => {
        if (unknownOutcome) {
          return;
        }
        setError(undefined);
        const intent = submitted ?? values;
        if (
          intent.trimIds.some(
            (trimId) =>
              !availableTrims.some(
                (trim) => trim.id === trimId && trim.modelYearId === intent.scope?.modelYearId
              )
          )
        ) {
          setError(
            t(
              'entity.newVehicle.authoring.invalidCarScope',
              'Select trims from the chosen model year before saving.'
            )
          );
          return;
        }
        setSubmitted(intent);
        try {
          if (!createdId.current) {
            // Without an idempotency key, an interrupted POST cannot safely be retried.
            setUnknownOutcome(true);
            const created = await createFeature({
              featureWriteRequestBody: equipmentWriteBody(intent, props.canViewCost),
            }).unwrap();
            createdId.current = created.id;
            setUnknownOutcome(false);
          }
          for (const trimId of intent.trimIds) {
            const trim = await getTrim({trimId}, false).unwrap();
            if (trim.modelYearId !== intent.scope?.modelYearId) {
              throw new Error('Selected trim moved outside the submitted model year.');
            }
            const assignments = await getAssignments({trimId}, false).unwrap();
            await putAssignments({
              trimId,
              replaceTrimFeatureAssignmentsRequestBody: {
                version: trim.version,
                assignments: [
                  ...assignments.filter((assignment) => assignment.featureId !== createdId.current),
                  {
                    featureId: createdId.current,
                    inclusion: intent.inclusion,
                    appliesIf: {anyOfVariantIds: [], allOfFeatureIds: []},
                    priceIf: [],
                    variantPriceOverrides: [],
                    priceDeltaNet: null,
                    sortOrder: assignments.length,
                  },
                ],
              },
            }).unwrap();
          }
          props.onSaved();
        } catch (caught) {
          if (!(caught instanceof Error)) {
            handleCatalogueError(caught as ApiError);
          }
          if (!createdId.current && isDefiniteNewVehicleRejection(caught)) {
            setUnknownOutcome(false);
            setSubmitted(null);
            setError(catalogueErrorMessage(caught));
            return;
          }
          setError(
            createdId.current
              ? t(
                  'entity.newVehicle.authoring.equipmentAttachFailed',
                  "The equipment item was saved, but some trim attachments weren't. Retry to finish attaching it — no duplicate item will be created."
                )
              : t(
                  'entity.newVehicle.authoring.equipmentOutcomeUnknown',
                  "We couldn't confirm whether the item was saved. Check the equipment library before trying again."
                )
          );
        }
      }}
    />
  );
}
